import { Component, Inject, Optional, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { Validators } from '@angular/forms';
import { MmAlertMode, MmAlertService, MmAlertSize, MmAlertType, MmDialogRefService } from '@metromarkets/components';
import { of } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';

import { NUMBER_1, NUMBER_3000 } from '~shared/constants';
import { FIELD_TYPES } from '~shared/modules/advanced-form/constants/field-types.constant';
import {
  AdvancedFormEventTypes,
  IAdvancedFormConfig,
  IAdvancedFormEvent,
} from '~shared/modules/advanced-form/models/advanced-form.model';
import { EMAIL_PATTERN, NO_METRO_DOMAIN } from '~shared/validators/pattern-validators';
import { isPlatformBrowser } from '@angular/common';
import { EmployeeService } from '~app/modules/authorization/services/employee.service';
import { TranslateService } from '@ngx-translate/core';
import { IdamRegisterTrackingService } from '~app/modules/authorization/services/idam-register-tracking.service';

const { required, pattern, compose } = Validators;

@Component({
  selector: 'app-create-digital-account-dialog',
  templateUrl: './create-digital-account-dialog.component.html',
  styleUrls: ['./create-digital-account-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CreateDigitalAccountDialogComponent {
  public advancedFormConfig: IAdvancedFormConfig;
  public phase = NUMBER_1;
  public error = null;
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() public dialogRef: MmDialogRefService<any>,
    private translateService: TranslateService,
    private alertService: MmAlertService,
    private readonly employeeService: EmployeeService,
    private idamRegisterTrackingService: IdamRegisterTrackingService,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.advancedFormConfig = {
        formSectionList: [
          {
            fieldList: [
              {
                name: 'email',
                label: 'REGULAR.FIELDS.CUSTOMER_EMAIL.LABEL',
                placeholder: 'REGULAR.FIELDS.CUSTOMER_EMAIL.PLACEHOLDER',
                type: FIELD_TYPES.email,
              },
            ],
          },
        ],
        actionList: [
          {
            extras: {
              color: 'secondary',
            },
            type: 'cancel',
            text: 'PAGES.AUTH.EMPLOYEE.CREATE_DIGITAL_ACCOUNT.DAILOG.EXIT.BUTTON.TEXT',
          },

          {
            type: 'submit',
            text: 'PAGES.AUTH.EMPLOYEE.CREATE_DIGITAL_ACCOUNT.DAILOG.SUBMIT.BUTTON.TEXT',
          },
        ],
        validationRules: {
          email: {
            required,
            pattern: compose([pattern(EMAIL_PATTERN), pattern(NO_METRO_DOMAIN)]),
          },
        },
        initialFormData: {
          email: this.dialogRef.containerInstance.config.data?.email ?? '',
        },
      };
    }
  }

  public onAdvancedFormEvents(event: IAdvancedFormEvent): void {
    if (event.type === AdvancedFormEventTypes.submit) {
      this.onSubmit(event).subscribe(() => {
        event.done();
        this.dialogRef.close();
      });
    }

    if (event.type === 'cancel') {
      this.onCancel();
    }
  }

  public onCancel() {
    this.dialogRef.close();
  }

  private onSuccess = (metroCard) => {
    const title = 'PAGES.AUTH.EMPLOYEE.CREATE_DIGITAL_ACCOUNT.DAILOG.ALERT.SUCCESS.TITLE.TEXT';
    const message = 'PAGES.AUTH.EMPLOYEE.CREATE_DIGITAL_ACCOUNT.DAILOG.ALERT.SUCCESS.DESCRIPTION.TEXT';
    this.translateService
      .get([title, message])
      .pipe(take(NUMBER_1))
      .subscribe((translations) => {
        this.alertService.addAlert({
          type: MmAlertType.success,
          title: translations[title],
          message: translations[message],
          mode: MmAlertMode.banner,
          size: MmAlertSize.large,
        });
      });

    setTimeout(() => {
      if (metroCard) {
        this.idamRegisterTrackingService.idamCardSignUpEventTracking(true);
      } else {
        this.idamRegisterTrackingService.idamSignUpEventTracking(true, true);
      }
      // 3 seconds delay to bypass default 3000ms throttleInterval of @browser-bunyan/server-stream
    }, NUMBER_3000);
  };

  private onError = (customerEmail: string, error) => {
    const title = 'PAGES.AUTH.EMPLOYEE.CREATE_DIGITAL_ACCOUNT.DAILOG.ALERT.ERROR.TITLE.TEXT';
    let message;
    if (
      error?.status === 400 ||
      error?.status === 422 ||
      ['customerEmail: Email is already in use'].includes(error?.detail)
    ) {
      message = 'PAGES.AUTH.EMPLOYEE.CREATE_DIGITAL_ACCOUNT.DAILOG.ALERT.ERROR.DESCRIPTION.TEXT';
    } else {
      message = 'PAGES.AUTH.EMPLOYEE.CREATE_DIGITAL_ACCOUNT.DAILOG.ALERT.ERROR.DESCRIPTION.TEXT1';
    }
    this.translateService
        .get([title, message], { email: customerEmail})
        .pipe(take(NUMBER_1))
        .subscribe((translations) => {
          this.alertService.addAlert({
            type: MmAlertType.error,
            title: translations[title],
            message: translations[message],
            mode: MmAlertMode.alert,
            size: MmAlertSize.large,
          });
        });
    this.idamRegisterTrackingService.idamSignUpErrorEventTracking(error?.detail || 'Failed to create digital identity');
  };

  private onSubmit(event: IAdvancedFormEvent) {
    const data: { customerCdmId: string; businessCdmId?: string, metroCard } = this.dialogRef.containerInstance.config.data;
    const customerEmail = event.payload.formData.email;

    return this.employeeService.impersonateCustomer(data.customerCdmId, data.businessCdmId, customerEmail).pipe(
      tap(() => this.onSuccess(data.metroCard)),
      catchError((error) => {
        this.onError(customerEmail, error);
        return of(null);
      })
    );
  }
}
